import { CURRICULUM } from '../data/curriculum';
import {
  CURRICULUM_MILESTONES,
  type CurriculumMilestone,
} from '../data/curriculumMilestones';
import { isTierPlanned } from './curriculum';
import type { CurriculumTierId, UserProfile } from '../types';

function trinPositionPct(trinIndex: number, trinCount: number): number {
  if (trinCount <= 1) return 0;
  return (trinIndex / (trinCount - 1)) * 100;
}

export function activeCurriculumTrinIds(): string[] {
  return CURRICULUM.filter((t) => !isTierPlanned(t)).flatMap((t) =>
    t.trins.map((tr) => tr.id),
  );
}

export function curriculumProgressFraction(user: UserProfile): number {
  const trinIds = activeCurriculumTrinIds();
  if (trinIds.length === 0) return 0;
  const done = trinIds.filter((id) => user.completedTrinIds.includes(id)).length;
  return done / trinIds.length;
}

/** 0–100: sidste gennemførte trin på tidslinjen (samme skala som milepæle). */
export function curriculumCompletedPositionPct(user: UserProfile): number {
  const trinIds = activeCurriculumTrinIds();
  if (trinIds.length === 0) return 0;

  let furthest = -1;
  for (const id of user.completedTrinIds) {
    const idx = trinIds.indexOf(id);
    if (idx > furthest) furthest = idx;
  }
  if (furthest < 0) return 0;
  return trinPositionPct(furthest, trinIds.length);
}

/** 0–100: hvor langt fremme i kurset brugeren er (aktivt trin på tidslinjen). */
export function curriculumPositionPct(user: UserProfile): number {
  const trinIds = activeCurriculumTrinIds();
  if (trinIds.length === 0) return 0;

  const currentIdx = trinIds.indexOf(user.currentTrinId);
  if (currentIdx >= 0) {
    return trinPositionPct(currentIdx, trinIds.length);
  }

  let furthest = 0;
  for (const id of user.completedTrinIds) {
    const idx = trinIds.indexOf(id);
    if (idx > furthest) furthest = idx;
  }
  return trinPositionPct(furthest, trinIds.length);
}

export function curriculumProgressLabel(fraction: number): string {
  return `${Math.round(fraction * 100)}%`;
}

export interface CurriculumMilestoneState extends CurriculumMilestone {
  /** 0–100 position on roadmap bar. */
  positionPct: number;
  completed: boolean;
  current: boolean;
}

export function curriculumMilestoneStates(
  user: UserProfile,
): CurriculumMilestoneState[] {
  const trinIds = activeCurriculumTrinIds();
  const indexById = new Map(trinIds.map((id, i) => [id, i]));
  const completed = new Set(user.completedTrinIds);

  return CURRICULUM_MILESTONES.filter((m) => indexById.has(m.trinId)).map(
    (m) => {
      const idx = indexById.get(m.trinId)!;
      const positionPct = trinPositionPct(idx, trinIds.length);
      const isCompleted = completed.has(m.trinId);
      return {
        ...m,
        positionPct,
        completed: isCompleted,
        current: user.currentTrinId === m.trinId && !isCompleted,
      };
    },
  );
}

export interface CurriculumTierBand {
  tierId: CurriculumTierId;
  title: string;
  startPct: number;
  endPct: number;
}

/** Baggrundsbånd for roadmap: hvor hvert niveau starter og slutter. */
export function curriculumTierBands(): CurriculumTierBand[] {
  const trinIds = activeCurriculumTrinIds();
  if (trinIds.length === 0) return [];

  const bands: CurriculumTierBand[] = [];
  for (const tier of CURRICULUM.filter((t) => !isTierPlanned(t))) {
    const indices = tier.trins
      .map((t) => trinIds.indexOf(t.id))
      .filter((i) => i >= 0);
    if (indices.length === 0) continue;
    const startIdx = Math.min(...indices);
    const endIdx = Math.max(...indices);
    bands.push({
      tierId: tier.id,
      title: tier.title.replace(/\s*\(planlagt\)\s*$/i, '').trim(),
      startPct: trinPositionPct(startIdx, trinIds.length),
      endPct: trinPositionPct(endIdx, trinIds.length),
    });
  }
  return bands;
}
